import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { cancellationRulesApi } from "@/dashboard/inventory_management/cancel/cancellationRulesApi";
import { createCancellationTargetLookup } from "@/dashboard/inventory_management/common/cancellationTargets";
import type { CancellationRegistryOptionsResponse } from "@/dashboard/inventory_management/common/cancellationTargetTypes";
import { defineStore } from "pinia";
import { computed, shallowRef } from "vue";

/**
 * The cancellation target regions from the registry-options API, fetched once per page load: they are the
 * same for every company and change only with a deploy.
 */
export const useCancellationTargetsStore = defineStore(
  "cancellationTargets",
  () => {
    const options = shallowRef<CancellationRegistryOptionsResponse | null>(
      null
    );
    let pending: Promise<void> | null = null;

    const lookup = computed(() =>
      createCancellationTargetLookup(options.value)
    );

    /**
     * Starts fetching the options unless they are loaded or already loading, so concurrent callers share one
     * request; a failed request is retried by the next call. Returns the request only to the caller that
     * started it, so its failure reaches exactly one caller.
     *
     * @throws The request's error, to the caller that started it
     */
    const load = (): Promise<void> | null => {
      if (options.value || pending) return null;
      pending = cancellationRulesApi
        .getRegistryOptions()
        .then((response) => {
          options.value = response.data;
        })
        .finally(() => {
          pending = null;
        });
      return pending;
    };

    return { lookup, load };
  }
);

/**
 * The cancellation target lookup for a component, loading the options on first use. A failed load goes to
 * the global error handler once, however many components waited on it; until the options arrive the lookup
 * knows no region.
 */
export const useCancellationTargets = () => {
  const store = useCancellationTargetsStore();
  const { globalErrorHandler } = useGlobalHandlers();
  store.load()?.catch(globalErrorHandler);
  return computed(() => store.lookup);
};
